import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { BookingShell, CenteredState, type SummaryRow } from '../components/BookingShell'
import { BookingApiError, createBooking, fetchAvailability, fetchBookingProfile } from '../api'
import { getGuestTimezone } from '../config'
import {
  BOOKING_CONTACT_LIMITS,
  type BookingDetailErrors,
  type BookingDetailField,
  validateBookingDetails,
} from '../lib/contact'
import {
  describeCalendarDate,
  describeDuration,
  describeLocation,
  formatLongDate,
  formatLongDateTime,
  formatTime,
} from '../lib/format'
import type {
  AppointmentConfirmation,
  AppointmentType,
  BookingAvailabilityResponse,
  BookingDaySlots,
  PublicBookingProfileResponse,
} from '../types'

type Step = 'type' | 'slot' | 'details' | 'confirmed'

const STEP_PROGRESS: Record<Step, number> = {
  type: 20,
  slot: 48,
  details: 76,
  confirmed: 100,
}

export function BookPage({ slug }: { slug: string }) {
  const guestTimezone = useMemo(() => getGuestTimezone(), [])

  const [profile, setProfile] = useState<PublicBookingProfileResponse | null>(null)
  const [profileError, setProfileError] = useState<string | null>(null)

  const [step, setStep] = useState<Step>('type')
  const [selectedType, setSelectedType] = useState<AppointmentType | null>(null)
  const [availability, setAvailability] = useState<BookingAvailabilityResponse | null>(null)
  const [availabilityLoading, setAvailabilityLoading] = useState(false)
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null)

  const [guestName, setGuestName] = useState('')
  const [guestEmail, setGuestEmail] = useState('')
  const [guestPhone, setGuestPhone] = useState('')
  const [guestNotes, setGuestNotes] = useState('')
  const [fieldErrors, setFieldErrors] = useState<BookingDetailErrors>({})

  const [submitting, setSubmitting] = useState(false)
  const submittingRef = useRef(false)
  const [error, setError] = useState<string | null>(null)
  const [confirmation, setConfirmation] = useState<AppointmentConfirmation | null>(null)

  useEffect(() => {
    let active = true
    fetchBookingProfile(slug)
      .then((result) => {
        if (active) setProfile(result)
      })
      .catch((cause: unknown) => {
        if (!active) return
        setProfileError(
          cause instanceof BookingApiError && cause.status === 404
            ? 'notFound'
            : cause instanceof Error ? cause.message : 'Something went wrong.',
        )
      })
    return () => {
      active = false
    }
  }, [slug])

  const loadAvailability = useCallback(
    (type: AppointmentType, signal?: AbortSignal) => {
      setAvailabilityLoading(true)
      setError(null)
      return fetchAvailability({ slug, appointmentTypeId: type.id, signal })
        .then((result) => {
          if (signal?.aborted) return
          setAvailability(result)
          const firstDay = result.days[0]?.date ?? null
          setSelectedDate((current) =>
            current && result.days.some((day) => day.date === current) ? current : firstDay,
          )
        })
        .catch((cause: unknown) => {
          if (signal?.aborted) return
          setError(cause instanceof Error ? cause.message : 'Could not load available times.')
        })
        .finally(() => {
          if (!signal?.aborted) setAvailabilityLoading(false)
        })
    },
    [slug],
  )

  const handleSelectType = (type: AppointmentType) => {
    setSelectedType(type)
    setSelectedSlot(null)
    setSelectedDate(null)
    setAvailability(null)
    setStep('slot')
    void loadAvailability(type)
  }

  const handleSubmit = async () => {
    if (!selectedType || !selectedSlot || submittingRef.current) return

    const validation = validateBookingDetails({
      name: guestName,
      email: guestEmail,
      phone: guestPhone,
      notes: guestNotes,
    })
    if (!validation.ok) {
      setFieldErrors(validation.errors)
      setError('Please check the highlighted details before confirming.')
      return
    }

    submittingRef.current = true
    setSubmitting(true)
    setFieldErrors({})
    setError(null)
    try {
      const result = await createBooking(slug, {
        appointmentTypeId: selectedType.id,
        start: selectedSlot,
        guestName: validation.value.name,
        guestEmail: validation.value.email,
        guestPhone: validation.value.phone,
        guestNotes: validation.value.notes,
        guestTimezone,
      })
      setConfirmation(result)
      setStep('confirmed')
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : 'Could not book that time.'
      setError(message)
      // A 409 means the slot went while the form was open — refresh the grid.
      if (cause instanceof BookingApiError && cause.status === 409 && selectedType) {
        setSelectedSlot(null)
        setStep('slot')
        void loadAvailability(selectedType)
      }
    } finally {
      submittingRef.current = false
      setSubmitting(false)
    }
  }

  if (profileError === 'notFound') {
    return (
      <CenteredState
        title="This booking page isn’t available"
        description="The link may be out of date, or the practice may have paused online booking. Please check with them directly."
      />
    )
  }

  if (profileError) {
    return <CenteredState title="Something went wrong" description={profileError} />
  }

  if (!profile) {
    return <CenteredState title="Loading" description="Fetching available times…" loading />
  }

  const summaryRows: SummaryRow[] = [
    {
      label: 'Session',
      value: selectedType?.name ?? 'Choose a session type',
      pending: !selectedType,
    },
    {
      label: 'Length',
      value: selectedType ? describeDuration(selectedType.durationMinutes) : '—',
      pending: !selectedType,
    },
    {
      label: 'When',
      value: selectedSlot
        ? formatLongDateTime(selectedSlot, guestTimezone)
        : 'Pick a time that suits you',
      pending: !selectedSlot,
    },
    {
      label: 'Where',
      value: confirmation?.meetingUrl
        ? (
          <a className="booking-meet-link" href={confirmation.meetingUrl} target="_blank" rel="noreferrer">
            Join on Google Meet
          </a>
        )
        : describeLocation(selectedType?.locationKind ?? ''),
      pending: !selectedType,
    },
  ]

  return (
    <BookingShell
      progress={STEP_PROGRESS[step]}
      practitionerName={profile.practitionerName}
      practitionerCredentials={profile.practitionerCredentials}
      practitionerImageUrl={profile.practitionerImageUrl}
      organizationName={profile.organizationName}
      summaryRows={summaryRows}
      statusPill={step === 'confirmed' ? { label: 'Confirmed', status: 'confirmed' } : undefined}
      headline={step === 'confirmed' ? 'You’re booked' : profile.headline}
      note={
        step === 'confirmed'
          ? `We’ve emailed ${confirmation?.guestEmail ?? 'you'} with the details and a calendar invitation.`
          : profile.welcomeMessage
      }
      onBack={
        step === 'slot'
          ? () => setStep('type')
          : step === 'details'
            ? () => setStep('slot')
            : undefined
      }
    >
      {step === 'type' && (
        <TypeStep profile={profile} onSelect={handleSelectType} />
      )}

      {step === 'slot' && selectedType && (
        <SlotStep
          typeName={selectedType.name}
          guestTimezone={guestTimezone}
          loading={availabilityLoading}
          availability={availability}
          selectedDate={selectedDate}
          selectedSlot={selectedSlot}
          onSelectDate={setSelectedDate}
          onSelectSlot={(slot) => {
            setSelectedSlot(slot)
            setStep('details')
          }}
        />
      )}

      {step === 'details' && selectedType && selectedSlot && (
        <DetailsStep
          slotLabel={formatLongDateTime(selectedSlot, guestTimezone)}
          guestName={guestName}
          guestEmail={guestEmail}
          guestPhone={guestPhone}
          guestNotes={guestNotes}
          fieldErrors={fieldErrors}
          submitting={submitting}
          onChange={{
            setGuestName,
            setGuestEmail,
            setGuestPhone,
            setGuestNotes,
          }}
          onFieldChange={(field) => {
            setFieldErrors((current) => ({ ...current, [field]: undefined }))
            setError(null)
          }}
          onSubmit={handleSubmit}
        />
      )}

      {step === 'confirmed' && confirmation && (
        <ConfirmedStep confirmation={confirmation} guestTimezone={guestTimezone} />
      )}

      {error && <div className="booking-error">{error}</div>}
    </BookingShell>
  )
}

function TypeStep({
  profile,
  onSelect,
}: {
  profile: PublicBookingProfileResponse
  onSelect: (type: AppointmentType) => void
}) {
  return (
    <>
      <h1 className="booking-title">Book with {profile.practitionerName}</h1>
      <p className="booking-description">
        {profile.welcomeMessage
          ?? 'Choose the kind of session you’d like, then pick a time that works for you.'}
      </p>

      {profile.appointmentTypes.length === 0 ? (
        <div className="booking-empty">
          This practice hasn’t published any session types yet. Please check back soon.
        </div>
      ) : (
        <div className="booking-type-list">
          {profile.appointmentTypes.map((type) => (
            <button
              key={type.id}
              type="button"
              className="booking-type"
              onClick={() => onSelect(type)}
            >
              <span className="booking-type-body">
                <strong>{type.name}</strong>
                {type.description && <small>{type.description}</small>}
              </span>
              <span className="booking-type-meta">{describeDuration(type.durationMinutes)}</span>
            </button>
          ))}
        </div>
      )}
    </>
  )
}

function SlotStep({
  typeName,
  guestTimezone,
  loading,
  availability,
  selectedDate,
  selectedSlot,
  onSelectDate,
  onSelectSlot,
}: {
  typeName: string
  guestTimezone: string
  loading: boolean
  availability: BookingAvailabilityResponse | null
  selectedDate: string | null
  selectedSlot: string | null
  onSelectDate: (date: string) => void
  onSelectSlot: (slot: string) => void
}) {
  const day: BookingDaySlots | undefined = availability?.days.find(
    (candidate) => candidate.date === selectedDate,
  )

  return (
    <>
      <h1 className="booking-title">Pick a time</h1>
      <p className="booking-description">
        {typeName} · times shown in your timezone ({guestTimezone.replace(/_/g, ' ')}).
      </p>

      {loading && (
        <div style={{ display: 'grid', gap: 8 }}>
          {[0, 1, 2, 3].map((index) => (
            <span className="booking-skeleton" key={index} />
          ))}
        </div>
      )}

      {!loading && availability && availability.days.length === 0 && (
        <div className="booking-empty">
          No open times in the next few weeks. Please contact the practice directly and they’ll
          find you a slot.
        </div>
      )}

      {!loading && availability && availability.days.length > 0 && (
        <>
          <div className="booking-date-strip" role="tablist" aria-label="Available dates">
            {availability.days.map((candidate) => {
              const parts = describeCalendarDate(candidate.date)
              return (
                <button
                  key={candidate.date}
                  type="button"
                  role="tab"
                  aria-selected={candidate.date === selectedDate}
                  className="booking-date"
                  data-selected={candidate.date === selectedDate}
                  onClick={() => onSelectDate(candidate.date)}
                >
                  <span className="booking-date-weekday">{parts.weekday}</span>
                  <span className="booking-date-day">{parts.day}</span>
                  <span className="booking-date-month">{parts.month}</span>
                </button>
              )
            })}
          </div>

          {day && (
            <div className="booking-slot-grid">
              {day.slots.map((slot) => (
                <button
                  key={slot.start}
                  type="button"
                  className="booking-slot"
                  data-selected={slot.start === selectedSlot}
                  onClick={() => onSelectSlot(slot.start)}
                >
                  {formatTime(slot.start, guestTimezone)}
                </button>
              ))}
            </div>
          )}

          <p className="booking-timezone-note">
            {availability.calendarSynced
              ? 'Times shown are free on your practitioner’s calendar.'
              : 'Times shown are open in this practice’s schedule.'}
          </p>
        </>
      )}
    </>
  )
}

function DetailsStep({
  slotLabel,
  guestName,
  guestEmail,
  guestPhone,
  guestNotes,
  fieldErrors,
  submitting,
  onChange,
  onFieldChange,
  onSubmit,
}: {
  slotLabel: string
  guestName: string
  guestEmail: string
  guestPhone: string
  guestNotes: string
  fieldErrors: BookingDetailErrors
  submitting: boolean
  onChange: {
    setGuestName: (value: string) => void
    setGuestEmail: (value: string) => void
    setGuestPhone: (value: string) => void
    setGuestNotes: (value: string) => void
  }
  onFieldChange: (field: BookingDetailField) => void
  onSubmit: () => void
}) {
  return (
    <>
      <h1 className="booking-title">Almost there</h1>
      <p className="booking-description">{slotLabel}</p>

      <form
        className="booking-field-stack"
        noValidate
        onSubmit={(event) => {
          event.preventDefault()
          onSubmit()
        }}
      >
        <div className="booking-field">
          <label htmlFor="guest-name">Your name</label>
          <input
            id="guest-name"
            name="name"
            autoComplete="name"
            value={guestName}
            onChange={(event) => {
              onChange.setGuestName(event.target.value)
              onFieldChange('name')
            }}
            placeholder="Alex Tremblay"
            maxLength={BOOKING_CONTACT_LIMITS.name}
            aria-invalid={Boolean(fieldErrors.name)}
            aria-describedby={fieldErrors.name ? 'guest-name-error' : undefined}
            required
          />
          <FieldError id="guest-name-error" message={fieldErrors.name} />
        </div>

        <div className="booking-field">
          <label htmlFor="guest-email">Email</label>
          <input
            id="guest-email"
            name="email"
            type="email"
            autoComplete="email"
            value={guestEmail}
            onChange={(event) => {
              onChange.setGuestEmail(event.target.value)
              onFieldChange('email')
            }}
            placeholder="alex@example.com"
            maxLength={BOOKING_CONTACT_LIMITS.email}
            aria-invalid={Boolean(fieldErrors.email)}
            aria-describedby={fieldErrors.email ? 'guest-email-error' : undefined}
            required
          />
          <FieldError id="guest-email-error" message={fieldErrors.email} />
        </div>

        <div className="booking-field">
          <label htmlFor="guest-phone">Phone (optional)</label>
          <input
            id="guest-phone"
            name="tel"
            type="tel"
            autoComplete="tel"
            value={guestPhone}
            onChange={(event) => {
              onChange.setGuestPhone(event.target.value)
              onFieldChange('phone')
            }}
            placeholder="+1 514 555 0134"
            maxLength={BOOKING_CONTACT_LIMITS.phone}
            aria-invalid={Boolean(fieldErrors.phone)}
            aria-describedby={
              fieldErrors.phone
                ? 'guest-phone-hint guest-phone-error'
                : 'guest-phone-hint'
            }
          />
          <p id="guest-phone-hint" className="booking-field-hint">
            Include the country code. Leave blank if you prefer email.
          </p>
          <FieldError id="guest-phone-error" message={fieldErrors.phone} />
        </div>

        <div className="booking-field">
          <label htmlFor="guest-notes">Anything you’d like them to know? (optional)</label>
          <textarea
            id="guest-notes"
            name="notes"
            value={guestNotes}
            onChange={(event) => {
              onChange.setGuestNotes(event.target.value)
              onFieldChange('notes')
            }}
            placeholder="What you’re hoping to get out of the session, allergies, anything relevant…"
            maxLength={BOOKING_CONTACT_LIMITS.notes}
            aria-invalid={Boolean(fieldErrors.notes)}
            aria-describedby={fieldErrors.notes ? 'guest-notes-error' : undefined}
          />
          <FieldError id="guest-notes-error" message={fieldErrors.notes} />
        </div>

        <button type="submit" className="booking-primary" disabled={submitting}>
          {submitting ? 'Booking…' : 'Confirm appointment'}
        </button>
      </form>
    </>
  )
}

function FieldError({ id, message }: { id: string; message?: string }) {
  if (!message) return null
  return (
    <p id={id} className="booking-field-error" role="alert">
      {message}
    </p>
  )
}

function ConfirmedStep({
  confirmation,
  guestTimezone,
}: {
  confirmation: AppointmentConfirmation
  guestTimezone: string
}) {
  const manageUrl = confirmation.manageToken
    ? `/appointments/manage?token=${encodeURIComponent(confirmation.manageToken)}`
    : null

  return (
    <>
      <h1 className="booking-title">You’re booked, {confirmation.guestName.split(' ')[0]}</h1>
      <p className="booking-description">
        {formatLongDate(confirmation.start, guestTimezone)} at{' '}
        {formatTime(confirmation.start, guestTimezone)} with {confirmation.practitionerName}.
      </p>

      <div className="booking-notice">
        A confirmation is on its way to {confirmation.guestEmail}, with a calendar invitation
        attached and a link to reschedule or cancel any time.
      </div>

      {confirmation.meetingUrl && (
        <a
          className="booking-primary"
          href={confirmation.meetingUrl}
          target="_blank"
          rel="noreferrer"
          style={{ textDecoration: 'none' }}
        >
          Join on Google Meet
        </a>
      )}

      {manageUrl && (
        <a
          className="booking-secondary"
          href={manageUrl}
          style={{ textDecoration: 'none' }}
        >
          Manage this appointment
        </a>
      )}
    </>
  )
}
